import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { QuebecObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { QuebecObstetricalFilePage, QuebecObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Quebec/QuebecObstetricalFilePage";
import { QuebecObstetricalFilePageFooter } from "@/Patient/Components/MedicalNote/Obstetric/Form/Quebec/QuebecObstetricalFilePageFooter";
import { BloodPressureField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/BloodPressureField";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { QuebecObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Quebec/QuebecObstetricalFileFieldLimits";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { BloodPressure } from "@/Patient/Models/MedicalSummary/BloodPressure";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { QuebecObstetricalFileFormPageTwo } from "@/Patient/Models/ObstetricalFile/Quebec/PageTwo/QuebecObstetricalFileFormPageTwo";
import { SexuallyTransmittedBloodBorneInfectionScreening } from "@/Patient/Models/ObstetricalFile/Quebec/PageTwo/SexuallyTransmittedBloodBorneInfectionScreening";
import { QuebecObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFileForm";

class QuebecObstetricalFilePageTwoState extends QuebecObstetricalFilePageState {
    public pageModel: QuebecObstetricalFileFormPageTwo;
    public vitalSign: ObstetricalFileVitalSign;
}

class QuebecObstetricalFilePageTwo extends QuebecObstetricalFilePage<QuebecObstetricalFilePageTwoState> {
    private readonly componentId = "obstetrical-file-page-two";

    private _vitalSignControlRenderer: ObstetricalFileControlRenderer;

    constructor(props: IObstetricalFilePageProps<QuebecObstetricalFileForm>) {
        super(props);
        this.setVitalSignControlRenderer(this.props.readonly);
    }

    protected get vitalSignControlRenderer(): ObstetricalFileControlRenderer {
        if (this._vitalSignControlRenderer?.getIsReadonly() !== this.props.readonly) {
            this.setVitalSignControlRenderer(this.props.readonly);
        }
        return this._vitalSignControlRenderer;
    }

    private setVitalSignControlRenderer(readonly: boolean): void {
        this._vitalSignControlRenderer = new ObstetricalFileControlRenderer({
            readonly: readonly,
            onBlur: () => this.onVitalSignUpdated()
        });
    }

    public componentDidMount(): void {
        if (this.state.pageModel.VitalSignEmrTemplateId)
            this.handleObstetricalFileVitalSignLink(this.state.pageModel.VitalSignEmrTemplateId);
    }

    protected buildState(props: IObstetricalFilePageProps<QuebecObstetricalFileForm>): QuebecObstetricalFilePageTwoState {
        const state = new QuebecObstetricalFilePageTwoState();

        state.form = new QuebecObstetricalFileForm(props.form);
        state.pageModel = state.form.PageTwo;

        state.vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
            obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
            emrTemplateId: state.pageModel.VitalSignEmrTemplateId,
            consultationDate: props.encounter.ConsultationDate,
            obstetricalFileContributionDate: props.contributionCreationDate,
            skipLoadLastHour:
                state.form.PageFour.PrenatalVisits?.any()
                && !!state.form.PageFour.PrenatalVisits[0].VitalSignEmrTemplateId
                && props.obstetricalFileMedicalSummary.getVitalSignBy(state.form.PageFour.PrenatalVisits[0].VitalSignEmrTemplateId, props.encounter.ConsultationDate) != null,
            weightUnit: state.pageModel.WeightUnit,
            heightUnit: state.pageModel.HeightUnit
        });
        if (state.form.isVitalSignReusedOnPageFour())
            state.form.PageFour.PrenatalVisits[0].VitalSignEmrTemplateId = state.vitalSign?.emrTemplateId;

        state.pageModel.VitalSignEmrTemplateId = state.vitalSign?.emrTemplateId;

        if (state.vitalSign.height !== state.pageModel.PreviousHeight) {
            state.form.PrePregnancyBodyMassIndex = state.vitalSign.height && state.form.WeightBeforePregnancyInKg
                ? Number(MedicalCalculator.calculateBodyMassIndex(state.form.WeightBeforePregnancyInKg, state.vitalSign.heightInMeters)?.toFixed(2))
                : null;
            state.pageModel.PreviousHeight = state.vitalSign.height;
        }

        return state;
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId}>
                {this.renderPhysicalExamSection()}
                {this.renderRiskAndTreatmentPlanSection()}
                {this.renderFooter()}
            </div>
        );
    }

    private renderPhysicalExamSection(): JSX.Element {
        const physicalExam = this.state.pageModel.PhysicalExam;
        const clickableTextContext = QuebecObstetricalFileClickableTextContext.PageTwo_Particularities;
        if (!physicalExam)
            return null;

        const isLatestMedicalItemInEmrTemplate = this.props.obstetricalFileMedicalSummary.isLatestMedicalItemInEmrTemplate(this.state.vitalSign);
        const latestPapTest = this.props.obstetricalFileMedicalSummary.getLatestPapTest(this.props.encounter);
        const particularitiesId = "particularities";

        return (
            <div className="section border border-top">
                <div className="row header">
                    <div className="col-xs-12">
                        <h4>{cacheStore.GetString("PhysicalExamLabel")}</h4>
                    </div>
                </div>
                <div className="physical-exam-first-row">
                    {this.renderField(
                        "HeightLabel",
                        <div className="unit-container">
                            {this.vitalSignControlRenderer.renderAutoCompleteTextbox({
                                value: this.state.vitalSign.height?.toString(),
                                autoCompleteItems: this.getHeightDropdownOptions(this.state.pageModel.HeightUnit),
                                onChange: value => this.handleHeightChange(value),
                                onAutoCompleteSelected: code => this.handleHeightChange(code),
                                maxLength: QuebecObstetricalFileFieldLimits.Page2.PhysicalExamHeight.MaxChars,
                                isNumeric: true,
                                isReadOnlyOverride: !isLatestMedicalItemInEmrTemplate,
                                className: `unit-value ${this.state.vitalSign.emrIsInvalid ? "invalid-vital-sign" : ""}`,
                                disableMatchAutoSorting: true
                            })}
                            {this.controlRenderer.renderHeightUnitInchMeterSelect(
                                this.state.pageModel.HeightUnit,
                                value => this.mutateModelState(s => {
                                    s.pageModel.HeightUnit = value;
                                    s.vitalSign.heightUnit = value;
                                }),
                                "unit"
                            )}
                        </div>,
                        "flex-column"
                    )}
                    {this.renderField(
                        "WeightBeforePregnancyLabel",
                        <div className="unit-container">
                            {this.controlRenderer.renderAutoCompleteTextbox({
                                value: this.state.form.WeightBeforePregnancy?.toString(),
                                autoCompleteItems: this.getWeightBeforePregnancyDropdownOptions(this.state.form.WeightBeforePregnancyUnit),
                                onChange: value => this.handleWeightBeforePregnancyChange(value),
                                onAutoCompleteSelected: code => this.handleWeightBeforePregnancyChange(code),
                                maxLength: QuebecObstetricalFileFieldLimits.Page2.PhysicalExamWeightBeforePregnancy.MaxChars,
                                isNumeric: true,
                                disableMatchAutoSorting: true,
                                className: "unit-value"
                            })}
                            {this.controlRenderer.renderWeightUnitSelect(
                                this.state.form.WeightBeforePregnancyUnit,
                                value => this.mutateModelState(s => s.form.setWeightBeforePregnancyUnit(value)),
                                "unit"
                            )}
                        </div>,
                        "flex-column"
                    )}
                    {this.renderField(
                        "CurrentWeightLabel",
                        <div className="unit-container">
                            {this.vitalSignControlRenderer.renderTextbox({
                                value: this.state.vitalSign.weight?.toString(),
                                onChange: value => this.mutateModelState(s => s.vitalSign.weight = value && Number(value)),
                                maxLength: QuebecObstetricalFileFieldLimits.Page2.PhysicalExamCurrentWeight.MaxChars,
                                isNumeric: true,
                                isReadOnlyOverride: !isLatestMedicalItemInEmrTemplate,
                                className: `unit-value ${this.state.vitalSign.emrIsInvalid ? "invalid-vital-sign" : ""}`
                            })}
                            {this.controlRenderer.renderWeightUnitSelect(
                                this.state.pageModel.WeightUnit,
                                value => this.mutateModelState(s => {
                                    s.pageModel.WeightUnit = value;
                                    s.vitalSign.weightUnit = value;
                                }),
                                "unit"
                            )}
                        </div>,
                        "flex-column"
                    )}
                    {this.renderField(
                        "PrePregnancyBodyMassIndexAbbrevLabel",
                        this.controlRenderer.renderTextbox({
                            value: isNaN(this.state.form.PrePregnancyBodyMassIndex) ? "" : this.state.form.PrePregnancyBodyMassIndex?.toString(),
                            onChange: value => this.mutateModelState(s => s.form.PrePregnancyBodyMassIndex = value && Number(value)),
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.PhysicalExamBMI.MaxChars,
                            isNumeric: true
                        }),
                        "flex-column"
                    )}
                    {this.renderField(
                        "BloodPressureLabel",
                        <BloodPressureField
                            readonly={this.props.readonly || !isLatestMedicalItemInEmrTemplate}
                            bloodPressure={this.state.vitalSign?.bloodPressure}
                            isInvalid={this.state.vitalSign.emrIsInvalid}
                            onBloodPressureChange={(bloodPressure: BloodPressure) => this.mutateModelState(s => s.vitalSign.bloodPressure = bloodPressure)}
                            onBlur={() => this.onVitalSignUpdated()}
                        />,
                        "flex-column"
                    )}
                    {this.renderField(
                        "ThyroidLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Thyroid,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Thyroid = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamThyroid.MaxChars
                        ),
                        "flex-column"
                    )}
                </div>
                <div className="row">
                    {this.renderField(
                        "NeckLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Neck,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Neck = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamNeck.MaxChars
                        ),
                        "col-xs-4"
                    )}
                    {this.renderField(
                        "HeartLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Heart,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Heart = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamHeart.MaxChars
                        ),
                        "col-xs-4"
                    )}
                    {this.renderField(
                        "LungsLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Lungs,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Lungs = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamLungs.MaxChars
                        ),
                        "col-xs-4"
                    )}
                </div>
                <div className="row">
                    {this.renderField(
                        "BreastsLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Breasts,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Breasts = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamBreasts.MaxChars
                        ),
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "AbdomenLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Abdomen,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Abdomen = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamAbdomen.MaxChars
                        ),
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "LowerLimbsLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.LowerLimbs,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.LowerLimbs = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamLowerLimbs.MaxChars
                        ),
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "PerineumLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Perineum,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Perineum = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamPerineum.MaxChars
                        ),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.renderField(
                        "VaginaLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Vagina,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Vagina = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamVagina.MaxChars
                        ),
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "CervixLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Cervix,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Cervix = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamCervix.MaxChars
                        ),
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "UterusLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Uterus,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Uterus = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamUterus.MaxChars
                        ),
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "AdnexaLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.Adnexa,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.Adnexa = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamAdnexa.MaxChars
                        ),
                        "col-xs-3"
                    )}
                </div>
                <div className="row">
                    {this.renderRadioButtonField(
                        "CytologyTestsDoneLabel",
                        <div>
                            {this.controlRenderer.renderYesNoRadio(
                                physicalExam.IsCytologyTestsDone,
                                value => this.mutateModelState(s => s.pageModel.PhysicalExam.IsCytologyTestsDone = value),
                                "Cytology"
                            )}
                            {latestPapTest &&
                                <span className="text-italic">{cacheStore.GetString("ObstetricalFile_LastCompletedPapTestLabel")} {new DateOnly(latestPapTest.CompletionDate).toString("yyyy-MM-dd")}</span>
                            }
                        </div>,
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "SexuallyTransmittedBloodBorneInfectionScreeningAbbrevLabel",
                        this.renderSexuallyTransmittedBloodBorneInfectionScreening(physicalExam.SexuallyTransmittedBloodBorneInfectionScreening),
                        "col-xs-3"
                    )}
                    {this.renderField(
                        "CorrespondenceBetweenUterineVolumeAndAgeOfPregnancyLabel",
                        this.controlRenderer.renderTextboxWithNormalOption(
                            physicalExam.CorrespondenceBetweenUterineVolumeAndAgeOfPregnancy,
                            value => this.mutateModelState(s => s.pageModel.PhysicalExam.CorrespondenceBetweenUterineVolumeAndAgeOfPregnancy = value),
                            QuebecObstetricalFileFieldLimits.Page2.PhysicalExamCorrespondenceBetweenUterineVolumeAndAgeOfPregnancy.MaxChars
                        ),
                        "col-xs-6 correspondence-field"
                    )}
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <span className="field-label">{cacheStore.GetString("ParticularitiesLabel")}</span>
                        {this.controlRenderer.renderTextarea({
                            value: physicalExam.Particularities,
                            onChange: value => this.mutateModelState(s => s.pageModel.PhysicalExam.Particularities = value),
                            id: particularitiesId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.PhysicalExamParticularities.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.PhysicalExamParticularities.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(particularitiesId, clickableTextContext)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderSexuallyTransmittedBloodBorneInfectionScreening(stbbiScreening: SexuallyTransmittedBloodBorneInfectionScreening): JSX.Element {
        return (
            <div>
                <div className="stbbi-checkbox">
                    <CheckboxControl
                        checked={stbbiScreening.IsGonorrhea}
                        disabled={this.props.readonly}
                        label={cacheStore.GetString("GonorrheaLabel")}
                        onChange={checked => this.mutateModelState(s => s.pageModel.PhysicalExam.SexuallyTransmittedBloodBorneInfectionScreening.IsGonorrhea = checked, () => this.handleFormUpdate())}
                    />
                </div>
                <div className="stbbi-checkbox">
                    <CheckboxControl
                        checked={stbbiScreening.IsChlamydia}
                        disabled={this.props.readonly}
                        label={cacheStore.GetString("ChlamydiaLabel")}
                        onChange={checked => this.mutateModelState(s => s.pageModel.PhysicalExam.SexuallyTransmittedBloodBorneInfectionScreening.IsChlamydia = checked, () => this.handleFormUpdate())}
                    />
                </div>
            </div>
        );
    }

    private renderRiskAndTreatmentPlanSection(): JSX.Element {
        const riskAndTreatment = this.state.pageModel.RiskFactorAndTreatment;
        if (!riskAndTreatment)
            return null;

        const pretermLabourPlanId = "PretermLabourPlan";
        const restrictedIntrauterineGrowthPlanId = "RestrictedIntrauterineGrowthPlan";
        const hypertensionPlanId = "HypertensionPlan";
        const diabetesPlanId = "DiabetesPlan";
        const maternalAgePlanId = "MaternalAgePlan";
        const congenitalAnormaliesPlanId = "CongenitalAnormaliesPlan";
        const presenceOfSexuallyTransmittedBloodBorneInfectionPlanId = "PresenceOfSexuallyTransmittedBloodBorneInfectionPlan";
        const pregnancyAfterInVitroFertilizationPlanId = "PregnancyAfterInVitroFertilizationPlan";
        const vaginalBirthAfterCesareanPlanId = "VaginalBirthAfterCesareanPlan";

        return (
            <div className="section section-risk-treatment border border-top">
                <div className="row header">
                    <div className="col-xs-12">
                        <h4>{cacheStore.GetString("RiskAndTreatmentPlanLabel")}</h4>
                    </div>
                </div>
                <div className="row sub-section sub-section-extra-detail">
                    <div className="col-xs-6">
                        <h5 className="sub-header"><strong>{cacheStore.GetString("RiskFactorsFullLabel")}</strong> <span className="text-lowercase">({cacheStore.GetString("DescriptionLabel")})</span></h5>
                        <p className="text-bold">{cacheStore.GetString("PretermLabourLabel")}</p>
                        <div className="row">
                            <div className="col-xs-4">
                                <ul className="dash text-lowercase">
                                    <li>{cacheStore.GetString("UterineAnomaliesLabel")}</li>
                                    <li>{cacheStore.GetString("ShortOrDilatedCervixLabel")}</li>
                                    <li>{cacheStore.GetString("MulitiplePregnancyLabel")}</li>
                                </ul>
                            </div>
                            <div className="col-xs-4">
                                <ul className="dash text-lowercase">
                                    <li>{cacheStore.GetString("IncompetentCervixLabel")}</li>
                                    <li>{cacheStore.GetString("InfectionLabel")}</li>
                                    <li>{cacheStore.GetString("ErgonomicRiskLabel")}</li>
                                </ul>
                            </div>
                            <div className="col-xs-4">
                                <ul className="dash text-lowercase">
                                    <li>{cacheStore.GetString("PreviousPrematureLabourOrDeliveryLabel")}</li>
                                </ul>
                            </div>
                        </div>
                        <div className="row">
                            <div className="other">
                                <ul className="dash text-lowercase">
                                    <li>
                                        {cacheStore.GetString("ObstetricalFile_OtherLabel")}
                                    </li>
                                </ul>
                                <div>
                                    {this.controlRenderer.renderTextbox({
                                        value: riskAndTreatment.PretermLabourOther,
                                        onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.PretermLabourOther = value),
                                        maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorPretermLabourOther.MaxChars
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xs-6">
                        <h5 className="sub-header"><strong>{cacheStore.GetString("PlanLabel")}</strong> <span className="text-lowercase">({cacheStore.GetString("RecommendationsLabel")}, {cacheStore.GetString("ManagementLabel")}, {cacheStore.GetString("EtcLabel")})</span></h5>
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.PretermLabourPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.PretermLabourPlan = value),
                            id: pretermLabourPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorPretermLabourPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorPretermLabourPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(pretermLabourPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_PretermLabour)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section sub-section-extra-detail">
                    <div className="col-xs-6">
                        <p className="text-bold">{cacheStore.GetString("RestrictedIntrauterineGrowthLabel")}</p>
                        <div className="row">
                            <div className="col-xs-4">
                                <ul className="dash text-lowercase">
                                    <li>{cacheStore.GetString("PreviousLowWeightBabyLabel")}</li>
                                    <li>{cacheStore.GetString("CigaretteSmokingLabel")}</li>
                                </ul>
                            </div>
                            <div className="col-xs-4">
                                <ul className="dash">
                                    <li>{cacheStore.GetString("MedicalConditionDiabetesHBPLabel")}</li>
                                    <li className="text-lowercase">{cacheStore.GetString("WeightGainLessThanEightKgAtTermLabel")}</li>
                                    <li className="text-lowercase">{cacheStore.GetString("UnderPrivilegedSocioeconStatusLabel")}</li>
                                </ul>
                            </div>
                            <div className="col-xs-4">
                                <ul className="dash text-lowercase">
                                    <li>{cacheStore.GetString("SmallSizeLessThanOnePointFiveMLabel")}</li>
                                    <li>{cacheStore.GetString("PregravidicWeightLessThanFortyFiveKgLabel")}</li>
                                </ul>
                            </div>
                        </div>
                        <div className="row">
                            <div className="other">
                                <ul className="dash text-lowercase">
                                    <li>
                                        {cacheStore.GetString("ObstetricalFile_OtherLabel")}
                                    </li>
                                </ul>
                                <div>
                                    {this.controlRenderer.renderTextbox({
                                        value: riskAndTreatment.RestrictedIntrauterineGrowthOther,
                                        onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.RestrictedIntrauterineGrowthOther = value),
                                        maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorRestrictedIntrauterineGrowthOther.MaxChars
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.RestrictedIntrauterineGrowthPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.RestrictedIntrauterineGrowthPlan = value),
                            id: restrictedIntrauterineGrowthPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorRestrictedIntrauterineGrowthPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorRestrictedIntrauterineGrowthPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(restrictedIntrauterineGrowthPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_RestrictedIntrauterineGrowth)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section">
                    <div className="col-xs-6">
                        <p className="text-bold">{cacheStore.GetString("HypertensionLabel")}</p>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.HypertensionPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.HypertensionPlan = value),
                            id: hypertensionPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorHypertensionPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorHypertensionPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(hypertensionPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_Hypertension)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section">
                    <div className="col-xs-6">
                        <p className="text-bold">{cacheStore.GetString("DiabetesLabel")}</p>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.DiabetesPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.DiabetesPlan = value),
                            id: diabetesPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorDiabetesPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorDiabetesPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(diabetesPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_Diabetes)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section">
                    <div className="col-xs-6">
                        <p className="text-bold">{cacheStore.GetString("MaternalAgeLabel")}</p>
                        <div className="row">
                            <div className="col-xs-12">
                                <ul className="dash text-lowercase">
                                    <li>{cacheStore.GetString("AdolescentLabel")}</li>
                                    <li>{cacheStore.GetString("AdvancedMaternalAgeLabel")}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.MaternalAgePlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.MaternalAgePlan = value),
                            id: maternalAgePlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorMaternalPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorMaternalPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(maternalAgePlanId, QuebecObstetricalFileClickableTextContext.PageTwo_MaternalAge)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section">
                    <div className="col-xs-6">
                        <p className="text-bold">{cacheStore.GetString("CongenitalAnomaliesLabel")}</p>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.CongenitalAnormaliesPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.CongenitalAnormaliesPlan = value),
                            id: congenitalAnormaliesPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorCongenitalAnomaliesPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorCongenitalAnomaliesPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(congenitalAnormaliesPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_CongenitalAbnomalies)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section sub-section-extra-detail">
                    <div className="col-xs-6">
                        <p className="text-bold">{cacheStore.GetString("PresenceOfSexuallyTransmittedBloodBorneInfectionScreeningAbbrevLabel")}</p>
                        <p className="text-bold">{cacheStore.GetString("PresenceOfSexuallyTransmittedBloodBorneInfectionScreeningAbbrevRiskFactorsLabel")}</p>
                        <div className="row">
                            <div className="col-xs-12">
                                <ul className="dash text-lowercase">
                                    <li>{cacheStore.GetString("NeedForRepeatScreeningLabel")}</li>
                                    <li>{cacheStore.GetString("NeedForRiskReductionConselingLabel")}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.PresenceOfSexuallyTransmittedBloodBorneInfectionPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.PresenceOfSexuallyTransmittedBloodBorneInfectionPlan = value),
                            id: presenceOfSexuallyTransmittedBloodBorneInfectionPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorPresenceOfSTBBIPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorPresenceOfSTBBIPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(presenceOfSexuallyTransmittedBloodBorneInfectionPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_PresenceOfSTBBI)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section">
                    <div className="col-xs-6">
                        <p className="text-bold" dangerouslySetInnerHTML={{ __html: cacheStore.GetString("PregnancyAfterInVitroFertilizationLabel") }} />
                        <p className="text-bold">{cacheStore.GetString("FertilityTreatmentLabel")}<span className="text-lowercase">, {cacheStore.GetString("EtcLabel")}.</span></p>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.PregnancyAfterInVitroFertilizationPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.PregnancyAfterInVitroFertilizationPlan = value),
                            id: pregnancyAfterInVitroFertilizationPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorPregnancyAfterInVitroFertilizationPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorPregnancyAfterInVitroFertilizationPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(pregnancyAfterInVitroFertilizationPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_FertilityTreatment)
                        })}
                    </div>
                </div>
                <div className="inner-border border border-top" />
                <div className="row sub-section sub-section-extra-detail">
                    <div className="col-xs-6">
                        <p className="text-bold">{cacheStore.GetString("VaginalBirthAfterCesareanAbbrevLabel")}</p>
                    </div>
                    <div className="col-xs-6">
                        {this.controlRenderer.renderTextarea({
                            value: riskAndTreatment.VaginalBirthAfterCesareanPlan,
                            onChange: value => this.mutateModelState(s => s.pageModel.RiskFactorAndTreatment.VaginalBirthAfterCesareanPlan = value),
                            id: vaginalBirthAfterCesareanPlanId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page2.RiskFactorVBACPlan.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page2.RiskFactorVBACPlan.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(vaginalBirthAfterCesareanPlanId, QuebecObstetricalFileClickableTextContext.PageTwo_VBAC)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        if (!this.state.pageModel.Signature)
            return null;

        return (
            <QuebecObstetricalFilePageFooter
                signature={this.state.pageModel.Signature}
                isInformationShared={this.state.pageModel.IsNeonatalBloodUrineInformationShared}
                informationLabel="QuebecNeonatalUrinaryAndBloodScreeningProgramLeafletLabel"
                readonly={this.props.readonly}
                onInformationSharedChange={isChecked => this.mutateModelState(s => s.pageModel.IsNeonatalBloodUrineInformationShared = isChecked)}
                onSignatureChange={value => this.mutateModelState(s => s.pageModel.Signature.Value = value, null, true)}
                onDateChange={date => this.mutateModelState(s => s.pageModel.Signature.Date = date, () => this.handleFormUpdate())}
                onFieldBlur={() => this.handleFormUpdate()}
            />
        );
    }

    private handleHeightChange(value: any): void {
        if (isNaN(value))
            return;

        this.mutateModelState(s => {
            s.vitalSign.height = value;
            s.pageModel.PreviousHeight = value;
            s.form.PrePregnancyBodyMassIndex = Number(MedicalCalculator.calculateBodyMassIndex(s.form.WeightBeforePregnancyInKg, s.vitalSign.heightInMeters)?.toFixed(2));
        });
    }

    private handleWeightBeforePregnancyChange(value: any): void {
        if (isNaN(value))
            return;

        this.mutateModelState(s => {
            s.form.WeightBeforePregnancy = value;
            s.form.PrePregnancyBodyMassIndex = Number(MedicalCalculator.calculateBodyMassIndex(s.form.WeightBeforePregnancyInKg, s.vitalSign.heightInMeters)?.toFixed(2));
        });
    }

    private mutateModelState(mutation: (state: QuebecObstetricalFilePageTwoState) => void, then?: () => void, ignoreSignature?: boolean): void {
        this.mutateState(
            s => {
                mutation(s);

                const currentUser = sessionStore.getCurrentEmployee();
                if (s.pageModel.Signature.UserContactFacetIds.any(id => id === currentUser.ContactFacetId))
                    return;

                s.pageModel.Signature.UserContactFacetIds.push(currentUser.ContactFacetId);
                s.pageModel.Signature.Date ||= this.props.encounter.ConsultationDate;

                if (!ignoreSignature)
                    s.pageModel.Signature.Value = limitTextLength((`${s.pageModel.Signature.Value}${(!!s.pageModel.Signature.Value ? ", " : "")}${currentUser.nameWithPrefix()}`), 65);
            },
            then
        );
    }

    private async onVitalSignUpdated(): Promise<void> {
        const updatedVitalSign = await super.handleVitalSignUpdate(
            this.state.vitalSign,
            this.state.pageModel.VitalSignEmrTemplateId
        );

        this.mutateState(
            s => {
                s.vitalSign.updateValueFromMedicalItemVitalSign(updatedVitalSign, s.pageModel.WeightUnit, s.pageModel.HeightUnit);
                s.pageModel.VitalSignEmrTemplateId = updatedVitalSign?.EMRTemplateId;
            },
            () => this.handleObstetricalFileVitalSignLink(updatedVitalSign?.EMRTemplateId)
        );
    }
}

export { QuebecObstetricalFilePageTwo };
